import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import {
  PublicClientApplication,
  EventType,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './authConfig';
import { App } from './App';
import './styles.css';

// Bootstrap is async but deliberately not top-level await: that compiles to a
// module format older browsers in Vite's default target cannot load at all.
async function bootstrap() {
  const msalInstance = new PublicClientApplication(msalConfig);

  // MSAL v3 requires an explicit initialize() before any other call, and
  // handleRedirectPromise() has to settle before the first render — otherwise
  // the response fragment from a login redirect is still sitting in the URL
  // when components start asking whether anyone is signed in, and the app
  // flashes the sign-in screen at an already-authenticated user.
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]!);
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const { account } = event.payload as AuthenticationResult;
      if (account) msalInstance.setActiveAccount(account);
    }
  });

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </MsalProvider>
    </React.StrictMode>
  );
}

bootstrap().catch((err: unknown) => {
  // Nothing has rendered yet at this point, so a thrown error would leave a
  // blank page with the reason only in the console.
  console.error('Failed to start AcuTidy', err);
  const root = document.getElementById('root');
  if (root) {
    root.textContent =
      err instanceof Error ? err.message : 'AcuTidy failed to start.';
  }
});
